import { daysBetween } from '~/calculations/calendar'
import {
  type DatedValue,
  earliestDate,
  projectionSeries,
  toSeries,
} from '~/calculations/chartSeries'
import type { Projection } from '~/calculations/projection'
import { LineChart } from '~/components/charts/LineChart'
import type { MeasurementSessionRecord } from '~/models/measurementSession'
import type { WeightDisplayUnit } from '~/models/settings'
import type { CalendarDate } from '~/types/dates'
import { formatCalendarDate, formatWeight, formatWeightValue } from '~/utils/format'

/**
 * The two trend charts on Progress (Checkpoint 5.1).
 *
 * Separated from `ProgressPage` because the page's job is loading and this
 * one's is shaping — and because a component that takes plain arrays can be
 * reasoned about without a database.
 *
 * **Every chart states its numbers in words underneath.** `ui-ux-spec.md`
 * requires a textual summary, and D-087 leaned on that requirement when it
 * chose a hand-rolled renderer: if the drawing is ever wrong, the sentence
 * beside it is still right.
 */

interface ProgressChartsProps {
  readonly observations: readonly { date: CalendarDate; weightKg: number }[]
  readonly sessions: readonly MeasurementSessionRecord[]
  readonly phaseStart: CalendarDate | undefined
  readonly projection: Projection | undefined
  readonly reference: CalendarDate
  readonly unit: WeightDisplayUnit
}

export function ProgressCharts({
  observations,
  sessions,
  phaseStart,
  projection,
  reference,
  unit,
}: ProgressChartsProps) {
  const weights: DatedValue[] = observations.map((entry) => ({
    date: entry.date,
    value: entry.weightKg,
  }))

  /* Only sessions that actually recorded a waist. A skipped site is absent from
     `sites` rather than zero (D-045), so this filter is the whole of it. */
  const waists: DatedValue[] = sessions
    .map((session) => ({ date: session.date, waist: session.sites.waistAtNavel?.medianCm }))
    .filter((entry): entry is { date: CalendarDate; waist: number } => entry.waist !== undefined)
    .map((entry) => ({ date: entry.date, value: entry.waist }))

  const origin = earliestDate(weights, waists)
  if (origin === undefined) {
    return (
      <p>
        Charts appear once you have recorded a few weigh-ins. Nothing is missing — there is just
        nothing to draw yet.
      </p>
    )
  }

  const weightSeries = toSeries(weights, origin)
  const waistSeries = toSeries(waists, origin)

  /* A phase that has not begun draws no boundary (D-107 §4). Without the upper
     bound the mark plots beyond every data point and the chart's accessible
     summary announces "Vertical marks: phase start" for a mark that is not on
     the chart — a false statement in the one line D-104 §4 calls least
     negotiable. The lower bound is the older rule: a phase that began before
     the first observation has no place to sit either. */
  const boundaries =
    phaseStart === undefined || phaseStart < origin || phaseStart > reference
      ? []
      : [{ x: daysBetween(origin, phaseStart), label: 'phase start' }]

  /* Anchored to the latest weigh-in, so the dashed line starts where the solid
     one ends rather than floating away from it. */
  const lastWeight = weightSeries.at(-1)
  const projected =
    projection === undefined || lastWeight === undefined
      ? []
      : projectionSeries(projection, lastWeight.x)

  const startLabel = formatCalendarDate(origin)
  const endLabel = formatCalendarDate(reference)

  return (
    <>
      <LineChart
        /* The axis shows bare numbers, so the unit is named once in the title. */
        title={`Weight (${unit})`}
        points={weightSeries}
        formatValue={(value) => formatWeightValue(value as never, unit)}
        summary={summariseWeight(weights, unit, projected.length > 0)}
        xStartLabel={startLabel}
        xEndLabel={endLabel}
        boundaries={boundaries}
        projection={projected}
        emptyMessage="No weigh-ins recorded yet."
      />

      <LineChart
        title="Waist (cm)"
        points={waistSeries}
        formatValue={(value) => `${value.toFixed(1)}`}
        summary={summariseWaist(waists)}
        xStartLabel={startLabel}
        xEndLabel={endLabel}
        boundaries={boundaries}
        emptyMessage="No waist measurements recorded yet. Waist is the primary evidence for a route, so it is worth recording."
      />
    </>
  )
}

/**
 * The sentence under the weight chart.
 *
 * States what was observed and, where relevant, that a dashed line is a
 * projection rather than a record — never colour or line style alone.
 */
function summariseWeight(
  values: readonly DatedValue[],
  unit: WeightDisplayUnit,
  hasProjection: boolean,
): string {
  if (values.length === 0) return 'No weigh-ins recorded yet.'

  const first = values[0]
  const last = values.at(-1)
  if (first === undefined || last === undefined) return 'No weigh-ins recorded yet.'

  const shown = `${values.length} ${values.length === 1 ? 'weigh-in' : 'weigh-ins'}`
  if (values.length === 1) {
    return `${shown}: ${formatWeight(first.value as never, unit)} on ${formatCalendarDate(first.date)}.`
  }

  const change = last.value - first.value
  const direction = change === 0 ? 'unchanged' : change < 0 ? 'down' : 'up'
  const magnitude = formatWeight(Math.abs(change) as never, unit)

  const projectionNote = hasProjection
    ? ' The dashed line is a projection from your recent rate, not a record of anything measured.'
    : ''

  return (
    `${shown} from ${formatWeight(first.value as never, unit)} on ${formatCalendarDate(first.date)} ` +
    `to ${formatWeight(last.value as never, unit)} on ${formatCalendarDate(last.date)} — ` +
    `${direction === 'unchanged' ? 'unchanged overall' : `${direction} ${magnitude} overall`}.` +
    projectionNote
  )
}

function summariseWaist(values: readonly DatedValue[]): string {
  if (values.length === 0) return 'No waist measurements recorded yet.'

  const first = values[0]
  const last = values.at(-1)
  if (first === undefined || last === undefined) return 'No waist measurements recorded yet.'

  if (values.length === 1) {
    return `One measurement: ${first.value.toFixed(1)} cm on ${formatCalendarDate(first.date)}.`
  }

  const change = last.value - first.value
  /* A change of zero and a missing change are different things, and the second
     is impossible here — both endpoints exist by construction. */
  const direction = change === 0 ? 'unchanged' : change < 0 ? 'down' : 'up'

  return (
    `${values.length} measurements from ${first.value.toFixed(1)} cm on ${formatCalendarDate(first.date)} ` +
    `to ${last.value.toFixed(1)} cm on ${formatCalendarDate(last.date)} — ` +
    `${direction === 'unchanged' ? 'unchanged overall' : `${direction} ${Math.abs(change).toFixed(1)} cm overall`}.`
  )
}
